import React, { useState } from 'react';
import { Eye, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import Tesseract from 'tesseract.js';
import { getDocument } from 'pdfjs-dist';
import '../../styles/design-system.css';

const OCRPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [language, setLanguage] = useState('eng');
    const [totalPages, setTotalPages] = useState(0);
    const [ocrResults, setOcrResults] = useState([]);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);
            setOcrResults([]);

            // Check if it's a PDF or image
            if (selectedFile.type === 'application/pdf') {
                try {
                    const arrayBuffer = await selectedFile.arrayBuffer();
                    const loadingTask = getDocument({ data: arrayBuffer });
                    const pdf = await loadingTask.promise;
                    setTotalPages(pdf.numPages);
                    toast.success(`PDF loaded: ${pdf.numPages} pages`);
                } catch (error) {
                    toast.error(`Failed to load PDF: ${error.message}`);
                }
            } else {
                setTotalPages(1);
                toast.success('Image loaded');
            }
        }
    };

    const performOCR = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(0);
            const results = [];

            if (file.type === 'application/pdf') {
                // Process PDF
                const arrayBuffer = await file.arrayBuffer();
                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;

                for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                    setProgress((pageNum / pdf.numPages) * 90);
                    toast.info(`Processing page ${pageNum}/${pdf.numPages}...`);

                    const page = await pdf.getPage(pageNum);
                    const viewport = page.getViewport({ scale: 2.0 });

                    // Render page to canvas
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    await page.render({
                        canvasContext: context,
                        viewport: viewport
                    }).promise;

                    // Perform OCR on canvas
                    const imageData = canvas.toDataURL('image/png');
                    const result = await Tesseract.recognize(imageData, language, {
                        logger: (m) => {
                            if (m.status === 'recognizing text') {
                                console.log(`Page ${pageNum}: ${Math.round(m.progress * 100)}%`);
                            }
                        }
                    });

                    results.push({
                        page: pageNum,
                        text: result.data.text,
                        confidence: result.data.confidence
                    });
                }
            } else {
                // Process single image
                setProgress(10);
                const imageUrl = URL.createObjectURL(file);

                const result = await Tesseract.recognize(imageUrl, language, {
                    logger: (m) => {
                        if (m.status === 'recognizing text') {
                            setProgress(10 + (m.progress * 80));
                        }
                    }
                });

                results.push({
                    page: 1,
                    text: result.data.text,
                    confidence: result.data.confidence
                });

                URL.revokeObjectURL(imageUrl);
            }

            setProgress(95);
            setOcrResults(results);

            // Create searchable PDF
            await createSearchablePDF(results);

            setProgress(100);
            toast.success('OCR completed successfully!');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('OCR error:', error);
            toast.error(`OCR failed: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const createSearchablePDF = async (results) => {
        try {
            const pdfDoc = await PDFDocument.create();
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

            for (const result of results) {
                const page = pdfDoc.addPage([595, 842]); // A4 size
                const { width, height } = page.getSize();

                // Add OCR text (visible for demonstration, would be invisible in production)
                const fontSize = 10;
                const lines = result.text.split('\n');
                let yPosition = height - 50;

                for (const line of lines) {
                    if (yPosition < 50) break; // Stop if we run out of space

                    page.drawText(line, {
                        x: 50,
                        y: yPosition,
                        size: fontSize,
                        font: font,
                        color: rgb(0, 0, 0),
                    });

                    yPosition -= fontSize + 5;
                }

                // Add page number and confidence
                page.drawText(`Page ${result.page} | Confidence: ${Math.round(result.confidence)}%`, {
                    x: 50,
                    y: 20,
                    size: 8,
                    font: font,
                    color: rgb(0.5, 0.5, 0.5),
                });
            }

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `ocr-searchable-${timestamp}.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

        } catch (error) {
            console.error('PDF creation error:', error);
            toast.error(`Failed to create searchable PDF: ${error.message}`);
        }
    };

    const downloadTextFile = () => {
        const allText = ocrResults.map(r =>
            `=== Page ${r.page} (Confidence: ${Math.round(r.confidence)}%) ===\n${r.text}\n\n`
        ).join('');

        const blob = new Blob([allText], { type: 'text/plain' });
        const timestamp = new Date().toISOString().slice(0, 10);
        saveAs(blob, `ocr-text-${timestamp}.txt`);
        toast.success('Text file downloaded');
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={ocrResults.length}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Eye size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    OCR PDF
                </h1>
                <p className="tool-description">
                    Extract text from scanned PDFs and images using OCR. Create searchable PDFs from scanned documents.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']}
                    multiple={false}
                    title="Drop scanned PDF or image here"
                    description="or click to browse (PDF, JPG, PNG supported)"
                />
            ) : (
                <Alert variant="success" title="File Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {totalPages > 0 && <Badge variant="info">{totalPages} page{totalPages !== 1 ? 's' : ''}</Badge>}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setOcrResults([]);
                                setTotalPages(0);
                            }}
                        >
                            Change File
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Settings */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                OCR Settings
                            </h3>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Language
                                </label>
                                <select
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    className="input"
                                >
                                    <option value="eng">English</option>
                                    <option value="spa">Spanish</option>
                                    <option value="fra">French</option>
                                    <option value="deu">German</option>
                                    <option value="ita">Italian</option>
                                    <option value="por">Portuguese</option>
                                    <option value="rus">Russian</option>
                                    <option value="chi_sim">Chinese (Simplified)</option>
                                    <option value="chi_tra">Chinese (Traditional)</option>
                                    <option value="jpn">Japanese</option>
                                    <option value="kor">Korean</option>
                                    <option value="ara">Arabic</option>
                                    <option value="hin">Hindi</option>
                                </select>
                            </div>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                            <p style={{
                                textAlign: 'center',
                                marginTop: 'var(--spacing-2)',
                                fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Processing OCR... This may take several minutes for large documents.
                            </p>
                        </div>
                    )}

                    {/* OCR Results */}
                    {ocrResults.length > 0 && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Card>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 'var(--spacing-4)'
                                }}>
                                    <h3 style={{
                                        fontSize: 'var(--text-lg)',
                                        fontWeight: 'var(--font-bold)'
                                    }}>
                                        OCR Results
                                    </h3>
                                    <Button variant="secondary" size="sm" onClick={downloadTextFile}>
                                        <Download size={16} />
                                        Download Text
                                    </Button>
                                </div>

                                <Alert variant="success" style={{ marginBottom: 'var(--spacing-4)' }}>
                                    Successfully extracted text from {ocrResults.length} page{ocrResults.length !== 1 ? 's' : ''}
                                </Alert>

                                <div style={{
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 'var(--spacing-3)'
                                }}>
                                    {ocrResults.map((result, index) => (
                                        <Card key={index} variant="outlined">
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: 'var(--spacing-2)'
                                            }}>
                                                <strong style={{ fontSize: 'var(--text-sm)' }}>Page {result.page}</strong>
                                                <Badge variant={result.confidence > 80 ? 'success' : result.confidence > 60 ? 'warning' : 'error'}>
                                                    {Math.round(result.confidence)}% confidence
                                                </Badge>
                                            </div>
                                            <div style={{
                                                fontSize: 'var(--text-sm)',
                                                color: 'var(--color-text-secondary)',
                                                maxHeight: '150px',
                                                overflowY: 'auto',
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: 'monospace',
                                                backgroundColor: 'var(--color-surface-elevated)',
                                                padding: 'var(--spacing-3)',
                                                borderRadius: 'var(--radius-md)'
                                            }}>
                                                {result.text || '(No text detected)'}
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    )}

                    {/* Process Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={performOCR}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Eye size={20} />
                            {ocrResults.length > 0 ? 'Process Again' : 'Start OCR Processing'}
                        </Button>
                    </div>

                    {/* Info Alert */}
                    <Alert variant="info" style={{ marginTop: 'var(--spacing-4)' }}>
                        OCR processing may take several minutes depending on document size and complexity.
                        The searchable PDF will be created automatically after OCR completes.
                    </Alert>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default OCRPDF;
